import * as React from "react";
import {useEffect, useState} from "react";
import {
  mainFormDataDefaults,maxBatchSize,
  maxUnitsToBake,
  prefermentDefaults, totalHydroMax, totalHydroMin,
  totalHydroSliderMax,
  totalHydroSliderMin
} from "./DoughFormDefaults";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Tooltip,
  Typography
} from "@mui/material";
import {FormTextField} from "../shared/FormTextField";
import * as Types from "../shared/Types";
import {
  getPrefermentPffPct,
  ingredientTypeGrain, ingredientTypeInclusion,
  ingredientTypeLiquid, ingredientTypeTopping,
  LEVAIN_PCT_TYPE_PFF,
  LEVAIN_PCT_TYPE_TOTAL_WT
} from "../shared/Types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import {calculateDoughRecipe} from "./DoughRecipeCalculator";
import {getLocalDate} from "../utils/DateUtils";
import {useNavigate, useParams} from "react-router";
import {SavedObjectDialog} from "../shared/SavedObjectDialog";
import {DoughRecipeWrapper} from "./DoughRecipeWrapper";
import {urlQueryParamConverted, urlRatioDough} from "../../App";
import {getNewStateKey} from "../utils/FormStateUtils";
import AttributionIcon from '@mui/icons-material/Attribution';
import ReplayIcon from '@mui/icons-material/Replay';
import IconButton from "@mui/material/IconButton";
import {ArrowDropDown, ArrowDropUp,} from "@mui/icons-material";
import {ContextualHelpDialog} from "../help/ContextualHelpDialog";
import {AttributionDialog} from "../shared/AttributionDialog";
import {PrefermentForm} from "./PrefermentForm";
import {MainDoughForm} from "./MainDoughForm";
import {sessionGetRaw, sessionRemove, storageGet, storageHasFormulaName, storagePut} from "../utils/StorageUtils";
import {useAuth} from "react-oidc-context";
import {FormulaLabelControl} from "../shared/FormulaLabelControl";
import {toFixedDecimal} from "../utils/NumberUtils";
import {migrateIngredientGroupToRecipePct} from "../utils/MigrationUtils";
import {ProcessForm} from "../shared/ProcessForm";
import {DoughProcess, getDefaultSn, processInitialMix, processInitMixSnFermentolyse} from "../shared/DoughProcess";
import {applyLockStateEffect} from "../shared/WakeLockFactory";
import {useSearchParams} from "react-router-dom";
import {
  clearSessionForm,
  doSaveSessionForm,
  getSessionForm,
  ratioSessionKey,
  saveSessionForm,
  weightSessionKey
} from "../utils/SessionManager";
import {SubMixForm} from "./SubMixForm";
import {FormulaSliderEditor} from "../shared/FormulaSliderEditor";
import {CostForm} from "./CostForm";
import {getFieldValuesAsUniqueArray} from "../utils/ArrayUtils";
import {getNormalizedFlourName, getNormalizedInclusionName} from "../shared/IngredientSupport";
import {unitFpUnit, unitPounds} from "../shared/Units";
import {getSubmixIngredientComponents} from "./SubmixSupport";
import {generateIngredientId} from "../utils/StringUtils";

const mainComponentId = 'main'
const prefermentComponentId = 'preferment'
const submixComponentId = 'submix'

/**
 * This is the main entry point form for  Ratio dough formulas, aka "Bakers Percent"
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function DoughForm(props) {
  const {wakeLock, setWakeLock} = props;
  const {formKey} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [savedFormName, setSavedFormName] = useState(() => initSavedFormName())
  const [savedFormData, setSavedFormData] = useState(() => initSavedFormData())
  const [sessionFormData, setSessionFormData] = useState(() => initSessionFormData())
  const [formData, setFormData] = useState(() => initFormData())
  const [prefermentFormData, setPrefermentFormData] = useState(() => initPrefermentFormData())
  const [ingredientMasterData, setIngredientMasterData] = useState(() => initIngredientMasterData())
  const [formNameError, setFormNameError] = useState(false)
  const [oldFormName, setOldFormName] = useState(undefined)
  const [recipeVisible, setRecipeVisible] = useState(false)
  const [recipeItems, setRecipeItems] = useState(undefined)
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(true)
  const [openFormExistsDialog, setOpenFormExistsDialog] = useState(false)
  const [openAttributionDialog, setOpenAttributionDialog] = useState(false)
  const [openContextualHelpDialog, setOpenContextualHelpDialog] = useState(false)
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [errorAlertMessage, setErrorAlertMessage] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [contextualHelpContent, setContextualHelpContent] = useState({title: undefined, content: undefined})
  const [formStateKey, setFormStateKey] = useState(getNewStateKey())
  const navigate = useNavigate()
  const auth = useAuth();

  useEffect(() => {
    applyLockStateEffect('DoughForm', recipeVisible, wakeLock, setWakeLock)
  }, [recipeVisible])

  function initSavedFormName() {
    if (formKey === undefined) {
      return undefined
    }
    return window.atob(formKey)
  }

  function initSavedFormData() {
    if (savedFormName === undefined) {
      return undefined
    }
    // try local storage
    const storedForm = storageGet(savedFormName)
    return storedForm ?? undefined
  }

  function initSessionFormData() {
    if (!!savedFormData) {
      // no session data if saved data is present
      return undefined
    }
    // look in session data for this form
    // Note that session storage is populated either by:
    //   - a previous visit here
    //   - set specially by the "Convert to %" button in the Weight Formula
    // The latter case uses session storage to hold the converted form and should guarantee the form name
    // will be distinct if the Weight formula was already persisted.
    // When navigating here via the "Convert to %" button, the "?converted" query param is applied to distinguish that use case
    const ratioSessionFormData = getSessionForm(ratioSessionKey)
    return ratioSessionFormData ?? undefined
  }

  function initFormData() {
    const savedOrSessionData = savedFormData ?? sessionFormData
    if (savedOrSessionData === undefined) {
      return mainFormDataDefaults
    }
    const updatedForm = migrateFormData(savedOrSessionData)
    // save updated form in session so navigating away and returning here will still present this one
    // to get the complete formula for the session save, apply preferment defaults here to limit session saves
    const mergedForm = {...updatedForm, prefermentData: (savedOrSessionData['includePreferment'] ?
              applyNewPrefermentFormDefaults(savedOrSessionData['prefermentData']) : undefined)}
    saveSessionForm(250, ratioSessionKey, savedOrSessionData['formName'], mergedForm)
    return updatedForm
  }

  function migrateFormData(savedOrSessionData) {
    // exclude preferment from the savedOrSessionData to split into separately managed states: formData and prefermentFormData
    // this is mostly being done to omit the preferment structure in the persisted data to save space
    // these are, of course, merged when calculating the recipe
    const {prefermentData, ...remainder} = savedOrSessionData
    if (prefermentData !== undefined ) {
      // migrate obsolete preferment field to new mainDough field
      if (Object.keys(prefermentData).includes('recipeFlourPct') && prefermentData['recipeFlourPct'] > 0) {
        remainder['prefermentPffPct'] = prefermentData['recipeFlourPct']
        delete prefermentData['recipeFlourPct']
      }
      // field obsolete in preferment data
      if (Object.keys(prefermentData).includes('bulkFermPct')) {
        delete prefermentData['bulkFermPct']
      }
      // field obsolete in preferment data
      if (Object.keys(prefermentData).includes('bulkFermTempF')) {
        delete prefermentData['bulkFermTempF']
      }
    }

    // add new and prune obsolete fields in main form...
    if (!Object.hasOwn(remainder, 'process')) {
      remainder['process'] = {...mainFormDataDefaults.process}
    }
    if (remainder['process'][processInitialMix] && !remainder['process'][processInitialMix]['sn']) {
      remainder['process'][processInitialMix]['sn'] = getDefaultSn(processInitialMix)
    } else if (remainder['process'][processInitialMix] && remainder['process'][processInitialMix]['sn'] === 'Fermentalyse') {
      // name changed from "...alyse" to "...olyse"
      remainder['process'][processInitialMix]['sn'] = processInitMixSnFermentolyse
    }
    if (!Object.hasOwn(remainder, 'submixes')) {
      remainder['submixes'] = [...mainFormDataDefaults.submixes]
    }
    if (!Object.hasOwn(remainder, 'costData')) {
      remainder['costData'] = [...mainFormDataDefaults.costData]
    }
    if (remainder['autolyse']) {
      remainder['process'][processInitialMix] = new DoughProcess(undefined, undefined, undefined, undefined)
    }
    if (Object.hasOwn(remainder, 'autolyse')) {
      delete remainder['autolyse']
    }
    if (Object.hasOwn(remainder, 'bulkFermPct')) {
      delete remainder['bulkFermPct']  // now part of process block but timing display will remain using state data
    }
    if (Object.hasOwn(remainder, 'bulkFermTempF')) {
      delete remainder['bulkFermTempF']  // now part of process block but timing display will remain using state data
    }
    if (Object.hasOwn(remainder, 'soaker')) {
      delete remainder['soaker']
    }

    if (remainder['includePreferment'] && remainder['prefermentPffPct'] > 0 && (remainder['prefermentPffPctType'] !== LEVAIN_PCT_TYPE_TOTAL_WT)) {
      // convert flour and liquid percentages from group-based within main dough to recipe-based bakers percentages
      migrateIngredientGroupToRecipePct(remainder['flour'], 'flour', 100 - remainder['prefermentPffPct'])
      const liquidDelta = toFixedDecimal((prefermentData['hydrationPct'] / 100) * remainder['prefermentPffPct'])
      migrateIngredientGroupToRecipePct(remainder['liquid'], 'liquid', 100 - liquidDelta)
    }
    // return only the main form for state initialization
    return applyNewMainFormDefaults(remainder)
  }

  function initPrefermentFormData() {
    const savedOrSessionData = savedFormData ?? sessionFormData
    if (savedOrSessionData === undefined) {
      return prefermentDefaults
    }
    return migratePrefermentFormData(savedOrSessionData)
  }

  function migratePrefermentFormData(savedOrSessionData) {
    if (savedOrSessionData['prefermentData'] === undefined) {
      return prefermentDefaults
    }
    return applyNewPrefermentFormDefaults(savedOrSessionData['prefermentData'])
  }

  function applyNewMainFormDefaults(storedForm) {
    if (storedForm['prefermentPffPctType'] === undefined) {
      // historically, all preferment formulas were PFF type
      storedForm['prefermentPffPctType'] = storedForm['includePreferment'] ? LEVAIN_PCT_TYPE_PFF : LEVAIN_PCT_TYPE_TOTAL_WT
    }
    if (storedForm['pffBakersPct'] === undefined) {
      storedForm['pffBakersPct'] = (storedForm['includePreferment'] && storedForm['prefermentPffPctType'] === LEVAIN_PCT_TYPE_PFF)
    }
    Object.keys(mainFormDataDefaults).filter(k => (k !== 'prefermentPffPctType' && k !== 'pffBakersPct')).forEach(k => {
      if (storedForm[k] === undefined) {
        storedForm[k] = mainFormDataDefaults[k]
      }
      // in case of type rename
      storedForm.formType = mainFormDataDefaults.formType
    })
    return storedForm
  }

  function applyNewPrefermentFormDefaults(storedForm) {
    Object.keys(prefermentDefaults).forEach(k => {
      if (storedForm[k] === undefined) {
        storedForm[k] = prefermentDefaults[k]
      }
    })
    return storedForm
  }

  function updateFormData(formData) {
    setFormData(formData)
    setHasChanges(true)
  }

  function updatePrefermentData(prefermentData) {
    setPrefermentFormData(prefermentData)
    setHasChanges(true)
  }

  function initIngredientMasterData(inputFormData, inputPrefermentFormData, inputSubmixData) {
    // init w params to allow for overriding state data, e.g. from reset button to avoid blocking for state update
    const srcFormData = inputFormData || formData
    const srcPrefermentFormData = inputPrefermentFormData || prefermentFormData
    const srcSubmixData = inputSubmixData || srcFormData.submixes

    const initIngredientDataMasterData = []
    const mainComponent = {componentId: mainComponentId, ingredientTypes: []}
    mainComponent.ingredientTypes.push({type: ingredientTypeGrain, names: getFieldValuesAsUniqueArray(srcFormData.flour, 'name')})
    mainComponent.ingredientTypes.push({type: ingredientTypeLiquid, names: getFieldValuesAsUniqueArray(srcFormData.liquid, 'name')})
    mainComponent.ingredientTypes.push({type: ingredientTypeInclusion, names: getFieldValuesAsUniqueArray(srcFormData.mixin, 'name')})
    mainComponent.ingredientTypes.push({type: ingredientTypeTopping, names: getFieldValuesAsUniqueArray(srcFormData.toppings, 'name')})
    initIngredientDataMasterData.push(mainComponent)
    if (srcFormData.includePreferment) {
      const prefermentComponent = {componentId: prefermentComponentId, ingredientTypes: []}
      prefermentComponent.ingredientTypes.push({
        type: ingredientTypeGrain,
        names: getFieldValuesAsUniqueArray(srcPrefermentFormData.flour, 'name')})
      prefermentComponent.ingredientTypes.push({
        type: ingredientTypeLiquid,
        names: getFieldValuesAsUniqueArray(srcPrefermentFormData.liquid, 'name')
      })
      prefermentComponent.ingredientTypes.push({
        type: ingredientTypeInclusion,
        names: getFieldValuesAsUniqueArray(srcPrefermentFormData.mixin, 'name')
      })
      initIngredientDataMasterData.push(prefermentComponent)
    }
    const submixComponents = getSubmixIngredientComponents(srcSubmixData, submixComponentId)
    return initIngredientDataMasterData.concat(submixComponents)
  }

  function updateIngredientList(componentId, ingredientData) {
    // incoming ingredientData: [{type: ingredientType, collection: []}, ... ]  i.e. handle multiple types each w/ own ingredients
    if (!componentId) {
      return
    }
    // console.log(`incoming ingredient update: componentId=${componentId}, ingredientData=${JSON.stringify(ingredientData)}`)
    const otherComponents = ingredientMasterData.filter(imd => imd['componentId'] !== componentId)
    let updatedIngredientTypes = []
    if (ingredientData?.length > 0) {
      const existingComponent = ingredientMasterData.find(imd => imd['componentId'] === componentId)
      const incomingTypes = ingredientData.map((ig) => ig['type'])
      const retainExistingTypes = existingComponent?.ingredientTypes.filter((igt) => !incomingTypes.includes(igt['type']))

      ingredientData.forEach(ig => {
        const ingredientCollection = ig.collection.filter((col) => !col['ro'])
        // skip this type if no names are provided (removal of last name for type)
        if (ingredientCollection.length > 0) {
          updatedIngredientTypes.push({type: ig.type, names: getFieldValuesAsUniqueArray(ingredientCollection, 'name')})
        }
      })
      if (retainExistingTypes?.length > 0) {
        updatedIngredientTypes = updatedIngredientTypes.concat(retainExistingTypes)
      }
    }
    // set new master state...
    let newIngredientMasterData = []
    if (updatedIngredientTypes.length === 0) {
      // no types, preexisting or otherwise, to update for this component
      newIngredientMasterData = otherComponents?.length > 0 ? otherComponents : []
    } else {
      newIngredientMasterData = otherComponents.concat({
        componentId: componentId,
        ingredientTypes: updatedIngredientTypes
      })
    }
    setIngredientMasterData(newIngredientMasterData)
  }

  function refreshCostDataIngredients() {
    // console.log(`refresh:: ingredientMasterData state: ${JSON.stringify(ingredientMasterData)}`)
    // console.log(`refresh:: costData BEFORE state: ${JSON.stringify(formData.costData)}`)
    const newCostData = []
    const uniqueTypesAndNames = {}
    const typesAndNames = ingredientMasterData.flatMap(imd => imd['ingredientTypes'])
    typesAndNames.forEach(tns => {
      const hasType = Object.hasOwn(uniqueTypesAndNames, tns['type'])
      if (!hasType) {
        uniqueTypesAndNames[tns['type']] = []
      }
      uniqueTypesAndNames[tns['type']] = uniqueTypesAndNames[tns['type']].concat(tns['names'])
    })

    Object.keys(uniqueTypesAndNames).forEach(typeName => {
      const transformFn = typeName === ingredientTypeGrain ? getNormalizedFlourName : getNormalizedInclusionName
      const normalizedNames = [...new Set(uniqueTypesAndNames[typeName].map(transformFn))]
      normalizedNames.forEach((nn) => {
        let priceAmount = 0
        let priceUnit = typeName === ingredientTypeTopping ? unitFpUnit : unitPounds
        const matchingCostData = formData.costData.find(cd => cd['type'] === typeName && cd['name'] === nn)
        if (matchingCostData !== undefined) {
          priceAmount = matchingCostData.priceAmount
          priceUnit = matchingCostData.priceUnit
        }
        let newIngredient = {type: typeName, name: nn, priceAmount: priceAmount, priceUnit: priceUnit}
        if (typeName === ingredientTypeTopping) {
          newIngredient = {...newIngredient, id: generateIngredientId('c')}
        }
        newCostData.push(newIngredient)
      })
    })
    // add existing costData topping names that don't exist in the master data...
    const ingredientMasterToppingNames = newCostData.filter(ncd => ncd['type'] === ingredientTypeTopping).map((ncd) => ncd['name'])
    const otherExistingCostToppings = formData.costData
    .filter(cd => cd['type'] === ingredientTypeTopping)
    .filter(cd => !ingredientMasterToppingNames.includes(cd['name']))
    updateFormData({...formData, costData: newCostData.concat(otherExistingCostToppings)})
    // console.log(`refresh:: costData AFTER state: ${JSON.stringify(newCostData)}`)
  }

  function updateCostData(updatedCostData, ingredientType) {
    const otherCostDataTypes = formData.costData.filter((cd) => cd.type !== ingredientType)
    const newCostData = otherCostDataTypes.slice()
    const updateCostDataWithTypes = []
    updatedCostData.forEach((cd) => {
      updateCostDataWithTypes.push({...cd, type: ingredientType})
    })
    updateFormData({...formData, costData: newCostData.concat(updateCostDataWithTypes)})
  }

  function hasCandidateNewName() {
    // note: formName field is disabled for saved forms so need to check only for new, unsaved forms only
    return (!formData.hasOwnProperty('createdAt')
            || searchParams.has(urlQueryParamConverted));
  }

  function isSavedForm() {
    return !searchParams.has(urlQueryParamConverted) && formData.hasOwnProperty('createdAt')
  }

  function onNumericFieldChange(evt) {
    let intVal = parseInt(evt.target.value)
    if (isNaN(intVal)) {
      intVal = 0
    }
    updateFormData({
      ...formData,
      [evt.target.name]: intVal
    })
  }

  function onNumericBlur(evt, minValue, maxValue) {
    let intVal = parseInt(evt.target.value)
    if (isNaN(intVal)) {
      intVal = 0
    }
    if (minValue !== undefined && intVal < minValue) {
      intVal = minValue
    } else if (maxValue !== undefined && intVal > maxValue) {
      intVal = maxValue
    }
    updateFormData({
      ...formData,
      [evt.target.name]: intVal
    })
  }

  function onStringFieldChange(evt) {
    updateFormData({
      ...formData,
      [evt.target.name]: evt.target.value
    })
  }

  function onIncludePrefermentChange(includePreferment) {
    updateFormData({
      ...formData,
      includePreferment: includePreferment,
      starterPct: (includePreferment ? 0 : mainFormDataDefaults.starterPct),
      starterHydrationPct: (includePreferment ? 0 : mainFormDataDefaults.starterHydrationPct),
      starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
      yeastPct: (includePreferment ? 0 : mainFormDataDefaults.yeastPct),
      yeastRatioId: (includePreferment ? 0 : mainFormDataDefaults.yeastRatioId),
      pffBakersPct: (includePreferment && formData.prefermentPffPctType === LEVAIN_PCT_TYPE_PFF),
      prefermentPffPct: (includePreferment ? getPrefermentPffPct(prefermentFormData.prefermentType) : 0),
      prefermentPffPctType: (includePreferment && formData.prefermentPffPctType === LEVAIN_PCT_TYPE_PFF ? LEVAIN_PCT_TYPE_PFF : LEVAIN_PCT_TYPE_TOTAL_WT)
    })

    let ingredientData = []
    if (includePreferment) {
      ingredientData.push({type: ingredientTypeGrain, collection: prefermentFormData.flour})
      ingredientData.push({type: ingredientTypeLiquid, collection: prefermentFormData.liquid})
      ingredientData.push({type: ingredientTypeInclusion, collection: prefermentFormData.mixin})
    }
    updateIngredientList(prefermentComponentId, ingredientData)
  }

  function notifyPrefermentTypeChange(prefermentType) {
    // only update pff pct in main if there was no preexisting value
    if (! formData.prefermentPffPct) {
      updateFormData({
        ...formData,
        prefermentPffPct: getPrefermentPffPct(prefermentType)
      })
    }
  }

  function onFormNameFieldChange(evt) {
    if (evt.target.validity.valid) {
      setFormNameError(false)
    } else {
      setFormNameError(true)
    }
    updateFormData({
      ...formData,
      [evt.target.name]: evt.target.value
    })
  }

  function onLabelsAdd(labelsToAdd) {
    if (labelsToAdd?.length > 0) {
      const newLabels = [...formData.labels]
      newLabels.push.apply(newLabels, labelsToAdd)
      updateFormData({
        ...formData,
        labels: newLabels
      })
    }
  }

  function onLabelRemove(deleteLabel) {
    updateFormData({
      ...formData,
      labels: formData.labels.filter(label => label !== deleteLabel)
    })
  }

  function recipeCloseRequest() {
    setRecipeVisible(false)
  }

  function openHelp(title, content) {
    setContextualHelpContent({title: title, content: content})
    setOpenContextualHelpDialog(true)
  }

  function onCloseHelp() {
    setOpenContextualHelpDialog(false)
    setContextualHelpContent({title: undefined, content: undefined})
  }

  function applyForm() {
    clearSessionForm(ratioSessionKey)
    // clear savedData reference to force reliance on session cache...
    // ...this would be overridden by Save, Revert or Clear button actions
    setSavedFormData(undefined)
    setSessionFormData(undefined)
    const mergedFormData = {...formData, prefermentData: (formData.includePreferment ? prefermentFormData : undefined)}
    setRecipeItems(calculateDoughRecipe(mergedFormData))
    setRecipeVisible(true)
    setSaveBtnDisabled(!hasChanges && isSavedForm())
    doSaveSessionForm(ratioSessionKey, formData.formName, mergedFormData)
    // if this is a converted form and user is taking the original weight formula name, erase the original weight formula session
    if (searchParams.has(urlQueryParamConverted) && formData.formName === sessionGetRaw(weightSessionKey)) {
      sessionRemove(weightSessionKey)
    }

  }

  function loadSavedForm() {
    const formName = formData.formName
    const storedForm = storageGet(formName)
    if (!storedForm) {
      console.error(`loadSavedForm: no saved form found for '${formName}', ignoring.`)
      return;
    }

    clearSessionForm(ratioSessionKey)
    const revertedFormData = migrateFormData(storedForm)
    const revertedPrefermentFormData = migratePrefermentFormData(storedForm)

    setFormNameError(false)
    setRecipeItems(undefined)
    setRecipeVisible(false)
    setSaveBtnDisabled(true)
    setFormStateKey(getNewStateKey())
    setHasChanges(false)
    setFormData(revertedFormData)
    setPrefermentFormData(revertedPrefermentFormData)

    // save reverted form in session so navigating away and returning here will still present this one
    const mergedForm = {...revertedFormData, prefermentData: (revertedFormData['includePreferment'] ?
              revertedPrefermentFormData : undefined)}
    doSaveSessionForm(ratioSessionKey, formName, mergedForm)
  }

  function resetForm() {
    clearSessionForm(ratioSessionKey)
    setSavedFormName(undefined)
    setSavedFormData(undefined)
    setSessionFormData(undefined)
    setFormData(mainFormDataDefaults)
    setPrefermentFormData(prefermentDefaults)
    // ingredient master can't wait for async state updates so pass in override defaults directly
    setIngredientMasterData(initIngredientMasterData(mainFormDataDefaults, prefermentDefaults, mainFormDataDefaults.submixes))
    setFormNameError(false)
    setRecipeItems(undefined)
    setRecipeVisible(false)
    setSaveBtnDisabled(true)
    setFormStateKey(getNewStateKey())
    setHasChanges(false)
    navigate(urlRatioDough)
  }

  function handleSaveClick() {
    saveForm(formData.formName, false)
  }

  function saveForm(formName, forceSave = false) {
    let formNameTrimmed = formName.trim()
    if (!forceSave && hasCandidateNewName() && storageHasFormulaName(formNameTrimmed)) {
      setOldFormName(formData.formName)
      setOpenFormExistsDialog(true)
      return
    }
    let timeStampField = 'createdAt'
    if (formData.hasOwnProperty(timeStampField)) {
      timeStampField = 'modifiedAt'
    }
    // this component tracks state of the main formData separately from the prefermentData, thus the two separate objects here
    const updatedFormData = {
      ...formData,
      formName: formNameTrimmed,
      [timeStampField]: getLocalDate().getTime()
    }
    // mergedFormData is complete formula including prefermentData for persisting
    const mergedFormData = {...updatedFormData, prefermentData: (formData.includePreferment ? prefermentFormData : undefined)}

    // update session before the subsequent state changes induce re-rendering!
    doSaveSessionForm(ratioSessionKey, formNameTrimmed, mergedFormData)
    // update local storage
    handleStoragePutError(storagePut(formNameTrimmed, mergedFormData, auth))

    setFormData(updatedFormData)
    setSavedFormData(mergedFormData)
    setRecipeItems(calculateDoughRecipe(mergedFormData))
    setSaveBtnDisabled(true)
    setHasChanges(false)
    setOldFormName(undefined)
    setSavedFormName(formNameTrimmed)

    if (searchParams.has(urlQueryParamConverted)) {
      // reset the URL if we came from the "Convert to %" button
      navigate(urlRatioDough)
    }
  }

  async function handleStoragePutError(storagePromise) {
    storagePromise.then((result) => {})
    .catch((e) => {
      console.error(`storagePut for ${formData.formName} completed in error: ${e}`)
      setErrorAlertMessage(`Error saving ${formData.formName} to account backend. Do a manual Sync on Saved Items menu option.`);
      setShowErrorAlert(true)
    })
  }

  function setNewFormName(newFormName) {
    updateFormData({...formData, formName: newFormName})
  }

  function applyNewFormName(newFormName) {
    saveForm(newFormName, true)
  }

  function applyAttribution(attributionText) {
    updateFormData({...formData, attribution: attributionText})
  }

  function incrementBatches() {
    const currVal = formData.numBatches
    if (currVal >= maxUnitsToBake) {
      return
    }
    updateFormData({...formData, numBatches: currVal + 1})
  }

  function decrementBatches() {
    const currVal = formData.numBatches
    if (currVal === 1) {
      return
    }
    updateFormData({...formData, numBatches: currVal - 1})
  }

  function dismissErrorAlert(event, reason) {
    setShowErrorAlert(false)
    setErrorAlertMessage('')
  }

  function renderMainHydration() {
    return (
            <FormulaSliderEditor displayLabel={'Total Hydration %'} fieldName={'hydrationPct'} fieldValue={formData.hydrationPct} fieldContainer={formData}
                                 updateFn={updateFormData} sliderColor={'water'} minVal={totalHydroMin} maxVal={totalHydroMax}
                                 sliderMinVal={totalHydroSliderMin} sliderMaxVal={totalHydroSliderMax} sliderStep={1}/>
    )
  }

  return (
      <div key={formStateKey}>
        <Grid container direction={'column'}>
          <Snackbar anchorOrigin={{vertical: 'top', horizontal: 'center'}} autoHideDuration={3000} open={showErrorAlert} onClose={dismissErrorAlert}>
            <Alert sx={{marginTop: '1rem'}} severity={'error'} onClose={dismissErrorAlert}>{errorAlertMessage}</Alert>
          </Snackbar>
        </Grid>

        <Grid container direction={'row'} paddingLeft={{xs: '5%', sm: '8%', md: '8%', lg: '20%'}} paddingRight={{xs: '5%', sm: '8%', md: '8%', lg: '20%'}} rowSpacing={1} columns={{xs: 4, sm: 8, md: 12}}>
          <Grid item container direction={'column'} marginTop={1.5} alignItems={'center'}>
            <Typography variant={'h5'}>% Bakers Percent Formula</Typography>
          </Grid>
          <Grid item container direction={'column'} justifyContent={'center'} paddingLeft={{xs: 0, sm: 0, md: 8}} paddingRight={{xs: 0, sm: 0, md: 8}}>
            <Grid item container direction={'row'} columns={{xs: 4, sm: 8, md: 12}}>
              <Grid item xs={3.5} sm={7.5} md={11.5}>
                <FormTextField sx={{width: '100%'}} required
                      id="outlined-required"
                      disabled={isSavedForm()}
                      size={'small'}
                      name={'formName'}
                      label="Name"
                      value={formData.formName}
                      onChange={onFormNameFieldChange}
                      error={formNameError}
                      helperText={
                        formNameError ? 'Formula name is required' : ''
                      }
                      variant="filled"/>
              </Grid>
              <Grid item xs={0.5} sm={0.5} md={0.5}>
                <Tooltip title={'Attribution'} placement={'bottom'} arrow>
                <IconButton onClick={() => setOpenAttributionDialog(true)}><AttributionIcon sx={{marginTop: 0.5, paddingLeft: 0.5}} color={'primary'}/></IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container direction={'row'} columns={{xs: 4, sm: 8, md: 12}} justifyContent={'center'}>
            <Grid item xs={1.5} sm={1.15} md={2.75} marginLeft={-2} marginRight={-2}>
                <FormTextField size={'small'}
                               sx={{width: '6ch'}}
                        id="batches-select"
                        label={'Units'}
                        name={'numBatches'} variant={'filled'}
                        value={formData.numBatches}
                        onChange={(evt) => onNumericFieldChange(evt)}
                        onBlur={(evt) => onNumericBlur(evt, 1, maxUnitsToBake)}/>
                <FormControl>
                  <IconButton onClick={incrementBatches} sx={{padding: 0, marginTop: -0.3}} color={"primary"}><ArrowDropUp fontSize={'large'}/></IconButton>
                  <IconButton onClick={decrementBatches} sx={{padding: 0, marginTop: -1.5}} color={"primary"}><ArrowDropDown fontSize={'large'}/></IconButton>
                </FormControl>
            </Grid>
            <Grid item xs={1} sm={1.25} md={2}>
              <FormTextField size={'small'} sx={{width: '8ch'}}
                      id="outlined-required"
                      name={'batchSize'} variant="filled"
                      label={formData.batchType === 'FW' ? 'Flour g' : 'Total g'}
                      value={formData.batchSize}
                      onChange={(evt) => onNumericFieldChange(evt)} onBlur={(evt) => onNumericBlur(evt, 20, maxBatchSize)}/>
            </Grid>
            <Grid item xs={1.5} sm={5.5} md={4.5}>
              <FormControl>
                <FormLabel id="batchTypeLabel" sx={{fontSize: '0.75rem', marginLeft: 1.75, marginBottom: -0.25}}>Unit Type</FormLabel>
                <RadioGroup aria-labelledby="batchTypeLabel" row
                            defaultValue={mainFormDataDefaults.batchType}
                            sx={{marginLeft: 0}}
                            value={formData.batchType}
                            name="batchType" onChange={onStringFieldChange}>
                  <FormControlLabel sx={{marginLeft: 0.15, marginRight: 0}} value={Types.BATCH_TYPE_TOTAL_WT}
                                    control={<Radio size={'small'} color={'primary'}/>}
                                    label={<Typography sx={{fontSize: 12}}>Total</Typography>}/>
                  <FormControlLabel sx={{marginLeft: 0, marginRight: 0}} value={Types.BATCH_TYPE_FLOUR_WT}
                                    control={<Radio size={'small'} color={'primary'}/>}
                                    label={<Typography sx={{fontSize: 12}}>Flour</Typography>}/>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <FormulaLabelControl existingFormLabels={formData.labels} onLabelsAdd={onLabelsAdd} onLabelRemove={onLabelRemove}/>

          {renderMainHydration()}

          <Grid item container direction={'column'}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                aria-controls="panel1a-content"
                                id="panel1a-header">
                <Typography>Preferment</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PrefermentForm formData={prefermentFormData} setFormData={updatePrefermentData} notifyPrefermentTypeChange={notifyPrefermentTypeChange}
                                includePrefermentValue={formData.includePreferment} includePrefermentChange={onIncludePrefermentChange}
                                updateIngredientList={updateIngredientList} componentId={prefermentComponentId}/>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item container direction={'column'}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                aria-controls="panel1a-content"
                                id="panel1a-header">
                <Typography>Main Dough</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MainDoughForm formData={formData} setFormData={updateFormData} prefermentLevainFermPct={prefermentFormData.levainFermPct}
                               prefermentHydrationPct={prefermentFormData.hydrationPct} prefermentStarterPct={prefermentFormData.starterPct}
                               updateIngredientList={updateIngredientList} componentId={mainComponentId}/>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item container direction={'column'}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                aria-controls="panel1a-content"
                                id="panel1a-header">
                <Typography>Supplemental Mixes</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SubMixForm formData={formData} setFormData={updateFormData} updateIngredientList={updateIngredientList} componentId={submixComponentId}/>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item container direction={'column'}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                aria-controls="panel1a-content"
                                id="panel1a-header">
                <Typography>Process & Notes</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ProcessForm formData={formData} setFormData={updateFormData}/>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item container direction={'column'}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                aria-controls="panel1a-content"
                                id="panel1a-header">
                <Typography>Costs</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CostForm costData={formData.costData} updateCostData={updateCostData} refreshCostDataIngredients={refreshCostDataIngredients}/>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item container spacing={2} direction={'row'} justifyContent={'center'} columns={{xs: 4, sm: 8, md: 12}}>
            {isSavedForm()   ?
              (
                <Grid item>
                  <Tooltip title={'Revert to Saved copy'} placement={'bottom'} arrow>
                    <span>
                    <IconButton sx={{marginRight: -1}} onClick={loadSavedForm} disabled={!hasChanges || formNameError}><ReplayIcon fontSize={'medium'} color={(!hasChanges || formNameError ? 'grey': 'primary')}/></IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              ) : null
            }
            <Grid item>
              <Button variant={'contained'} onClick={applyForm} disabled={formNameError}>Apply</Button>
            </Grid>
            <Grid item>
              <Button color={"secondary"} variant={'outlined'} onClick={resetForm}>Clear</Button>
            </Grid>
            <Grid item>
              <Button color={"secondary"} variant={'outlined'} disabled={saveBtnDisabled}
                      onClick={handleSaveClick}>Save</Button>
            </Grid>
          </Grid>
          <Grid item container direction={'column'} justifyContent={'center'} columns={{xs: 4, sm: 8, md: 12}}>
            <Paper elevation={3} sx={{marginBottom: '1.5rem'}}>
              <DoughRecipeWrapper items={recipeItems} visible={recipeVisible} closeRequest={recipeCloseRequest}/>
            </Paper>
          </Grid>
        </Grid>
        <SavedObjectDialog objectName={formData.formName}
                           setNewObjectName={setNewFormName}
                           apply={applyNewFormName}
                           existingFormName={oldFormName}
                           dialogOpen={openFormExistsDialog}
                           setDialogOpen={setOpenFormExistsDialog}
                           objectType={'Formula'} />
        <ContextualHelpDialog isOpen={openContextualHelpDialog} close={onCloseHelp} title={contextualHelpContent.title} content={contextualHelpContent.content}/>
        <AttributionDialog attributionText={formData.attribution} setNewAttributionText={applyAttribution} dialogOpen={openAttributionDialog} setDialogOpen={setOpenAttributionDialog}/>
      </div>
  )
}

export {DoughForm}
